import { useState, useEffect } from "react";
import { Clock } from "lucide-react";
import type { WalkthroughStreamStatus } from "@/hooks/use-walkthrough-stream";

interface LiveStepTimerProps {
  status: WalkthroughStreamStatus;
  isStalled: boolean;
  isRunning: boolean;
  isComplete: boolean;
  isErrored: boolean;
  stepStartedAt?: number;
  activeActionStartedAt?: number;
  runnerName?: string | null;
  isLocalLive: boolean;
  isBehindLive: boolean;
  stepsBehindLive: number;
}

export function formatElapsedSeconds(seconds: number): string {
  if (seconds <= 0) return "< 1s";
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs.toString().padStart(2, "0")}s`;
}

export function LiveStepTimer({
  status,
  isStalled,
  isRunning,
  isComplete,
  isErrored,
  stepStartedAt,
  activeActionStartedAt,
  runnerName,
  isLocalLive,
  isBehindLive,
  stepsBehindLive,
}: LiveStepTimerProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isRunning || isComplete || isErrored) return;
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, [isRunning, isComplete, isErrored]);

  // Use action start timestamp if an action is currently executing, else step start
  const effectiveStartTime = activeActionStartedAt ?? stepStartedAt;
  const elapsedSec = effectiveStartTime
    ? Math.max(0, Math.floor((now - effectiveStartTime) / 1000))
    : 0;

  return (
    <div className="flex items-center gap-2.5">
      {/* Stream connection status */}
      {isBehindLive ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
          Behind live · {stepsBehindLive} step{stepsBehindLive !== 1 ? "s" : ""}
        </span>
      ) : status === "reconnecting" ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-severity-warning/15 px-2 py-0.5 text-xs font-medium text-severity-warning">
          <span
            aria-hidden="true"
            className="size-1.5 rounded-full bg-severity-warning animate-pulse"
          />
          Reconnecting…
        </span>
      ) : isStalled ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-severity-warning/15 px-2 py-0.5 text-xs font-medium text-severity-warning">
          <span
            aria-hidden="true"
            className="size-1.5 rounded-full bg-severity-warning"
          />
          Stream stalled
        </span>
      ) : status === "error" ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-mark-critical/15 px-2 py-0.5 text-xs font-medium text-severity-critical">
          <span
            aria-hidden="true"
            className="size-1.5 rounded-full bg-severity-critical"
          />
          Disconnected
        </span>
      ) : isLocalLive ? (
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            aria-hidden="true"
            className="size-1.5 rounded-full bg-mark-helps animate-pulse"
          />
          Agent session · Live
        </span>
      ) : runnerName ? (
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            aria-hidden="true"
            className="size-1.5 rounded-full bg-mark-helps animate-pulse"
          />
          {runnerName}
        </span>
      ) : status === "streaming" && isRunning ? (
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            aria-hidden="true"
            className="size-1.5 rounded-full bg-mark-helps animate-pulse"
          />
          Live
        </span>
      ) : null}

      {/* Elapsed step stopwatch */}
      {isRunning && !isComplete && !isErrored && effectiveStartTime && (
        <span
          className="inline-flex items-center gap-1 font-mono text-xs tabular-nums text-muted-foreground"
          title="Elapsed time for current step / action"
        >
          <Clock className="size-3 text-muted-foreground" aria-hidden="true" />
          <span>{formatElapsedSeconds(elapsedSec)}</span>
        </span>
      )}
    </div>
  );
}
